import React from "react";

import { ArrayWrapperProps } from "@autoform/react";
import { PlusIcon } from "lucide-react";

import { CuratedBadge } from "@/components/field-curation/curated-badge";
import { Button } from "@/components/ui/button";

/**
 * The array's own dotted field path.
 *
 * `@autoform/react`'s `ArrayField` gives `ArrayWrapper` no `path` prop of its
 * own — only `field`, which is just the leaf key and would match a curated
 * name by coincidence for an array nested inside another object (e.g. an
 * unmodelled `metadata.faq`). What it DOES give us is one `AutoFormField`
 * element per item, each carrying the item's own full path (via
 * `ArrayElementWrapper`'s `children` prop) — `[...arrayPath, index]` — so
 * dropping the last (index) segment recovers the array's own path. A `.` in
 * it means this array is nested, and `useFieldCuration` already refuses to
 * match those. An empty array has no item to read a path from, so this returns ""
 * and the caller falls back to `field.key` — assuming top-level, the same
 * assumption `CustomObjectWrapper` makes for an object with no children yet.
 */
function arrayFieldPath(children: React.ReactNode): string {
  const items = children as
    | { props?: { children?: { props?: { path?: string[] } } } }[]
    | undefined;
  const path = items?.[0]?.props?.children?.props?.path;
  return path ? path.slice(0, -1).join(".") : "";
}

export const ArrayWrapper: React.FC<ArrayWrapperProps> = ({
  label,
  children,
  field,
  onAddItem,
}) => {
  const fieldKey = arrayFieldPath(children) || field.key;
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium">
        {label}
        <CuratedBadge fieldKey={fieldKey} />
      </h3>
      {children}
      <Button onClick={onAddItem} variant="outline" size="sm" type="button">
        <PlusIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};
